if (!customElements.get('cart-gift-boxes')) {
  customElements.define(
    'cart-gift-boxes',
    class CartGiftBoxes extends HTMLElement {
      connectedCallback() {
        this.onToggle = this.onToggle.bind(this);
        this.onInteract = this.onInteract.bind(this);
        this.addEventListener('change', this.onToggle);
        this.addEventListener('pointerdown', this.onInteract);
        this.addEventListener('focusin', this.onInteract);
        this.hideError();
        this.relinkThenClean();
        requestAnimationFrame(() => {
          if (typeof window.initCartGiftBoxesJump === 'function') {
            window.initCartGiftBoxesJump();
          }
        });
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onToggle);
        this.removeEventListener('pointerdown', this.onInteract);
        this.removeEventListener('focusin', this.onInteract);
      }

      onInteract() {
        this.cartDrawer()?.lockFrameScroll?.();
      }

      onToggle(event) {
        if (this._suppressToggle || !event.isTrusted) return;

        const input = event.target.closest('[data-cart-gift-box-toggle]');
        if (!input || input.disabled) return;

        event.stopPropagation();
        input.blur();
        this.cartDrawer()?.lockFrameScroll?.();
        this.hideError();

        if (input.checked) {
          this.addBox(input);
        } else {
          this.removeBox(input);
        }
      }

      cartDrawer() {
        return document.querySelector('cart-drawer');
      }

      sectionIds() {
        const cart = this.cartDrawer();
        if (!cart || typeof cart.getSectionsToRender !== 'function') {
          return ['cart-drawer', 'cart-icon-bubble'];
        }

        return cart
          .getSectionsToRender()
          .map((section) => section.id)
          .filter((id, index, list) => list.indexOf(id) === index);
      }

      setLoading(isLoading) {
        this.classList.toggle('is-loading', isLoading);
        this.querySelectorAll('[data-cart-gift-box-toggle]').forEach((input) => {
          input.disabled = isLoading || input.hasAttribute('data-unavailable');
        });
      }

      hideError() {
        const error = this.querySelector('[data-cart-gift-boxes-error]');
        if (!error) return;
        error.hidden = true;
        error.classList.add('visually-hidden');
        error.removeAttribute('role');
      }

      showError(input, checked) {
        if (input) {
          this._suppressToggle = true;
          input.checked = checked;
          this._suppressToggle = false;
        }
        const error = this.querySelector('[data-cart-gift-boxes-error]');
        if (!error) return;
        error.hidden = false;
        error.classList.remove('visually-hidden');
        error.setAttribute('role', 'alert');
      }

      refresh(data) {
        const cart = this.cartDrawer();
        if (!this.patchDrawer(data) && cart && typeof cart.renderContents === 'function' && data.sections) {
          cart.renderContents(data, { preserveScroll: true });
        }
        cart?.lockFrameScroll?.();
        if (document.activeElement?.hasAttribute?.('data-cart-gift-box-toggle')) {
          document.activeElement.blur();
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'gift-boxes',
            cartData: data,
          });
        }
      }

      // Swaps only the blocks that changed so the drawer never repaints as a whole.
      patchDrawer(data) {
        const drawer = this.cartDrawer();
        if (!drawer || typeof drawer.patchContents !== 'function') return false;

        const scroller = drawer.querySelector('cart-drawer-items');
        const scrollTop = scroller ? scroller.scrollTop : 0;

        if (!drawer.patchContents(data, { preserveScroll: true })) return false;
        if (typeof drawer.restoreItemsScroll === 'function') {
          drawer.restoreItemsScroll(scrollTop);
        }

        return true;
      }

      addBox(input) {
        const variantId = Number(input.dataset.variantId || this.dataset.variantId);
        if (!variantId || !input.dataset.itemKey || input.hasAttribute('data-unavailable')) {
          this.showError(input, false);
          return;
        }

        this.setLoading(true);

        fetch(`${routes.cart_add_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                properties: {
                  _gift_box_for: input.dataset.itemKey,
                  _gift_box_slot: input.dataset.slot || '1',
                  Para: input.dataset.productTitle || '',
                },
              },
            ],
            sections: this.sectionIds(),
            sections_url: window.location.pathname,
          }),
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.status) {
              this.showError(input, false);
              return;
            }
            this.refresh(data);
          })
          .catch(() => {
            this.showError(input, false);
          })
          .finally(() => {
            this.setLoading(false);
          });
      }

      removeBox(input) {
        const lineKey = input.dataset.lineKey;
        const line = Number(input.dataset.line);
        if (!lineKey && !line) {
          this.showError(input, true);
          return;
        }

        this.setLoading(true);

        const payload = {
          quantity: 0,
          sections: this.sectionIds(),
          sections_url: window.location.pathname,
        };
        if (lineKey) {
          payload.id = lineKey;
        } else {
          payload.line = line;
        }

        fetch(`${routes.cart_change_url}`, {
          ...fetchConfig(),
          body: JSON.stringify(payload),
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.status || data.errors) {
              this.showError(input, true);
              return;
            }
            this.refresh(data);
          })
          .catch(() => {
            this.showError(input, true);
          })
          .finally(() => {
            this.setLoading(false);
          });
      }

      removeOrphans() {
        const raw = (this.dataset.orphanKeys || '').replace(/,+$/, '');
        if (!raw || window.__removingGiftBoxOrphans) return;

        const keys = raw.split(',').filter(Boolean);
        if (!keys.length) return;

        window.__removingGiftBoxOrphans = true;
        const updates = {};
        keys.forEach((key) => {
          updates[key] = 0;
        });

        fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({
            updates,
            sections: this.sectionIds(),
            sections_url: window.location.pathname,
          }),
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.status || data.errors) return;
            this.refresh(data);
          })
          .finally(() => {
            window.__removingGiftBoxOrphans = false;
          });
      }

      relinkThenClean() {
        this.relinkBoxes()
          .catch(() => {})
          .finally(() => this.removeOrphans());
      }

      // Shopify can rewrite a line-item key when quantity changes. Gift boxes
      // store the old key in `_gift_box_for`; retarget them to the live line
      // with the same variant instead of deleting the box.
      async relinkBoxes() {
        if (window.__relinkingGiftBoxes) return;

        const cart = await fetch(`${routes.cart_url}.js`).then((response) => response.json());
        const jewelry = cart.items.filter(
          (item) => item.handle !== 'caixa-de-joias' && !item.properties?._gift_box_for
        );
        const liveKeys = new Set(jewelry.map((item) => item.key));
        const claimed = new Set();
        const relinks = [];

        cart.items.forEach((box) => {
          const forKey = box.properties?._gift_box_for;
          if (!forKey) return;
          if (liveKeys.has(forKey)) {
            claimed.add(`${forKey}:${box.properties._gift_box_slot || '1'}`);
            return;
          }

          const variantId = Number(String(forKey).split(':')[0]);
          const slot = String(box.properties._gift_box_slot || '1');
          const parent = jewelry.find((item) => {
            if (Number(item.variant_id || item.id) !== variantId) return false;
            return Number(item.quantity) >= Number(slot);
          });
          if (!parent) return;
          const claim = `${parent.key}:${slot}`;
          if (claimed.has(claim)) return;
          claimed.add(claim);
          if (parent.key === forKey) return;
          relinks.push({
            key: box.key,
            properties: {
              _gift_box_for: parent.key,
              _gift_box_slot: slot,
              Para: parent.product_title || box.properties.Para || '',
            },
          });
        });

        if (!relinks.length) return;

        window.__relinkingGiftBoxes = true;
        try {
          let lastData = null;
          for (const relink of relinks) {
            const response = await fetch(`${routes.cart_change_url}`, {
              ...fetchConfig(),
              body: JSON.stringify({
                id: relink.key,
                quantity: 1,
                properties: relink.properties,
                sections: this.sectionIds(),
                sections_url: window.location.pathname,
              }),
            });
            lastData = await response.json();
            if (lastData.status || lastData.errors) return;
          }
          if (lastData) this.refresh(lastData);
        } finally {
          window.__relinkingGiftBoxes = false;
        }
      }
    }
  );
}

let cartGiftBoxesJumpState = null;

function teardownCartGiftBoxesJump() {
  if (!cartGiftBoxesJumpState) return;
  cartGiftBoxesJumpState.observer?.disconnect();
  cartGiftBoxesJumpState.scroller?.removeEventListener('scroll', cartGiftBoxesJumpState.update);
  if (cartGiftBoxesJumpState.jump && cartGiftBoxesJumpState.onJump) {
    cartGiftBoxesJumpState.jump.removeEventListener('click', cartGiftBoxesJumpState.onJump);
  }
  if (cartGiftBoxesJumpState.heading && cartGiftBoxesJumpState.onJump) {
    cartGiftBoxesJumpState.heading.removeEventListener('click', cartGiftBoxesJumpState.onJump);
  }
  cartGiftBoxesJumpState = null;
}

function initCartGiftBoxesJump() {
  teardownCartGiftBoxesJump();

  const section = document.querySelector('cart-drawer [data-cart-gift-boxes]:not(.visually-hidden)');
  const jump = document.querySelector('[data-cart-gift-boxes-jump]');
  const scroller = document.querySelector('cart-drawer cart-drawer-items');
  const heading = section?.querySelector('[data-cart-gift-boxes-heading]');

  if (!section || !jump || !scroller) {
    if (jump) jump.hidden = true;
    if (typeof window.syncCartDrawerFooterSlot === 'function') {
      window.syncCartDrawerFooterSlot();
    }
    return;
  }

  const scrollToSection = () => {
    const offset = scroller.scrollTop + (section.getBoundingClientRect().top - scroller.getBoundingClientRect().top);
    scroller.scrollTo({ top: Math.max(0, offset - 6), behavior: 'smooth' });
  };

  const update = () => {
    const scrollerRect = scroller.getBoundingClientRect();
    const sectionRect = section.getBoundingClientRect();
    const visible = sectionRect.bottom > scrollerRect.top + 10 && sectionRect.top < scrollerRect.bottom - 10;
    jump.hidden = visible;
    if (typeof window.syncCartDrawerFooterSlot === 'function') {
      window.syncCartDrawerFooterSlot();
    }
  };

  const observer = new IntersectionObserver(update, {
    root: scroller,
    threshold: [0, 0.05, 0.2],
  });
  observer.observe(section);
  scroller.addEventListener('scroll', update, { passive: true });
  jump.addEventListener('click', scrollToSection);
  heading?.addEventListener('click', scrollToSection);
  update();

  cartGiftBoxesJumpState = {
    observer,
    scroller,
    jump,
    heading,
    update,
    onJump: scrollToSection,
  };
}

window.initCartGiftBoxesJump = initCartGiftBoxesJump;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartGiftBoxesJump);
} else {
  initCartGiftBoxesJump();
}
